import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { db } from '../lib/db'
import { useAuth } from '../contexts/AuthContext'

// Simple edit view for an existing standalone round. Loads one row from
// round_scores (own + non-attested + standalone only — enforced server-side),
// lists every hole with its score, lets the user change any value, saves
// via update_own_round_scores which recomputes totals.
//
// Deliberately NOT a re-mount of ScorecardPage — the scorecard has scored_by
// gates, active_rounds lifecycle, GPS integration, etc. that don't apply
// when you're just fixing a keyed-wrong score after the fact.

export default function EditRoundPage() {
  const { round_score_id } = useParams()
  const navigate = useNavigate()
  const { player } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [round, setRound] = useState(null)
  const [scores, setScores] = useState({})
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  useEffect(() => {
    if (!round_score_id || !player?.id) return
    let cancelled = false
    ;(async () => {
      try {
        const { data, error } = await db('get_own_round_for_edit', {
          round_score_id, player_id: player.id,
        })
        if (cancelled) return
        if (error) {
          setError(error)
        } else {
          setRound(data)
          const hs = typeof data.hole_scores === 'string' ? JSON.parse(data.hole_scores) : (data.hole_scores || {})
          setScores(hs)
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load round')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [round_score_id, player?.id])

  const updateHole = (holeNumber, val) => {
    setDirty(true)
    setScores(prev => {
      const next = { ...prev }
      const cleaned = val.replace(/[^0-9]/g, '')
      if (cleaned === '') delete next[String(holeNumber)]
      else next[String(holeNumber)] = Number(cleaned)
      return next
    })
  }

  const save = async () => {
    setSaving(true)
    setToast('')
    try {
      const { data, error } = await db('update_own_round_scores', {
        round_score_id, player_id: player.id, hole_scores: scores,
      })
      if (error) {
        setToast(`Save failed: ${error}`)
      } else {
        setDirty(false)
        setToast('Saved')
        setRound(prev => prev ? { ...prev, total_score: data.total_score, score_vs_par: data.score_vs_par } : prev)
        setTimeout(() => setToast(''), 1800)
      }
    } catch (e) {
      setToast(`Save failed: ${e.message || e}`)
    } finally {
      setSaving(false)
    }
  }

  if (!player?.id) return null

  if (loading) return (
    <div className="page"><div className="container" style={{ paddingTop: 40, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
    </div></div>
  )

  if (error || !round) return (
    <div className="page"><div className="container" style={{ paddingTop: 40 }}>
      <h1 style={{ marginBottom: 12 }}>Can't edit this round</h1>
      <p className="text-muted" style={{ marginBottom: 24 }}>{error || 'Round not found.'}</p>
      <button className="btn btn-ghost" onClick={() => navigate('/')}>← Back to Home</button>
    </div></div>
  )

  const holes = Array.isArray(round.holes_data) ? round.holes_data : []
  const totalPar = holes.reduce((s, h) => s + (h.par || 0), 0)
  const currentTotal = Object.values(scores).reduce((s, v) => s + (Number(v) || 0), 0)
  const currentVsPar = holes.reduce((acc, h) => {
    const s = Number(scores[String(h.hole_number)])
    if (!Number.isFinite(s) || s <= 0) return acc
    return acc + (s - (h.par || 0))
  }, 0)
  const enteredCount = Object.values(scores).filter(v => Number.isFinite(Number(v)) && Number(v) > 0).length

  return (
    <div className="page">
      <div className="container" style={{ paddingBottom: 96 }}>
        <div style={{ paddingTop: 20, paddingBottom: 12, borderBottom: '1px solid var(--green-mid)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>← Home</button>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold)', marginBottom: 2 }}>Edit Round</p>
            <h1 style={{ fontSize: '1.4rem', margin: 0 }}>{round.course_name || 'Round'}</h1>
            {round.club_name && round.club_name !== round.course_name && (
              <p className="text-muted text-sm" style={{ marginTop: 2 }}>{round.club_name}</p>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
          <div>
            <p className="text-muted text-sm" style={{ marginBottom: 4 }}>Total</p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', fontWeight: 700 }}>{currentTotal || '—'}</p>
          </div>
          <div>
            <p className="text-muted text-sm" style={{ marginBottom: 4 }}>vs Par</p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', fontWeight: 700 }}>
              {enteredCount === 0 ? '—' : (currentVsPar === 0 ? 'E' : currentVsPar > 0 ? `+${currentVsPar}` : String(currentVsPar))}
            </p>
          </div>
          <div>
            <p className="text-muted text-sm" style={{ marginBottom: 4 }}>Thru</p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', fontWeight: 700 }}>{enteredCount}/{holes.length}</p>
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          {holes.map((h, idx) => (
            <div key={h.hole_number} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 80px 80px', gap: 8, alignItems: 'center', padding: '10px 14px', borderBottom: idx < holes.length - 1 ? '1px solid var(--green-mid)' : 'none' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.95rem' }}>{h.hole_number}</span>
              <span className="text-muted text-sm">Par {h.par || '?'}</span>
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min={1}
                value={scores[String(h.hole_number)] ?? ''}
                onChange={e => updateHole(h.hole_number, e.target.value)}
                className="input"
                style={{ textAlign: 'center', padding: '6px 8px', fontFamily: 'var(--font-mono)', fontSize: '1rem' }}
                placeholder="—"
              />
              {(() => {
                const s = Number(scores[String(h.hole_number)])
                const par = Number(h.par)
                if (!Number.isFinite(s) || s <= 0 || !par) return <span />
                const d = s - par
                const txt = d === 0 ? 'E' : d > 0 ? `+${d}` : String(d)
                const color = d < 0 ? 'var(--blue-birdie)' : d > 0 ? 'var(--red)' : 'var(--gray-500)'
                return <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', color, textAlign: 'right' }}>{txt}</span>
              })()}
            </div>
          ))}
        </div>

        <p className="text-muted text-xs" style={{ marginTop: 12, textAlign: 'center' }}>
          Course par: {totalPar || '—'}
        </p>

        <div style={{ position: 'fixed', bottom: 'calc(76px + env(safe-area-inset-bottom))', left: 0, right: 0, padding: '0 16px', pointerEvents: 'none' }}>
          <div style={{ maxWidth: 'var(--container-max, 640px)', margin: '0 auto', pointerEvents: 'auto' }}>
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={save}
              disabled={!dirty || saving}
              style={{ boxShadow: '0 6px 18px rgba(0,0,0,0.2)' }}
            >
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
            </button>
          </div>
        </div>

        {toast && (
          <div style={{
            position: 'fixed', bottom: 'calc(140px + env(safe-area-inset-bottom))', left: 0, right: 0, textAlign: 'center',
            pointerEvents: 'none',
          }}>
            <span style={{
              display: 'inline-block', padding: '10px 16px', borderRadius: 8,
              background: 'rgba(0,0,0,0.85)', color: 'white', fontSize: '0.9rem',
            }}>{toast}</span>
          </div>
        )}
      </div>
    </div>
  )
}
